import { type SpeakerRef, SpeakerViewScreen } from '@carboncopyco/components';
import type { SpeakerPanelData } from '@carboncopyco/types/desktop';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SpeakerPanelEventsEnum } from '../../shared/enums/SpeakerPanelEventsEnum';
import useElectronListener from '../hooks/useElectronListener';

const SpeakerPanelScreen = () => {
  const speakerViewRef = useRef<SpeakerRef>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [speakerPanelData, setSpeakerPanelData] = useState<SpeakerPanelData>();
  const speakerPanelIdRef = useRef<number | null>(null);
  const [showSpeakerPanel, setShowSpeakerPanel] = useState(false);
  const [activeItem, setActiveItem] = useState(0);

  const handleClose = async (options?: {
    resetData: boolean;
  }) => {
    if (!speakerPanelIdRef.current || !speakerViewRef?.current) {
      return;
    }
    setShowSpeakerPanel(false);
    const { isNotesOpen, isWindowsOpen, getChildTime } = speakerViewRef.current;
    const updatedData = options?.resetData
      ? {
          isNotesOpen: false,
          isWindowOpen: false,
          currentTime: 0,
          isOpen: false,
        }
      : {
          isNotesOpen,
          isWindowOpen: isWindowsOpen,
          currentTime: getChildTime(),
          isOpen: true,
        };
    await window.dsDesktop.speakerPanel.updateData(
      speakerPanelIdRef.current,
      updatedData
    );
    await window.dsDesktop.speakerPanel.close();
    if (options?.resetData) {
      await window.dsDesktop.speakerPanel.setToggleButtonState(false);
    }
  };

  const updateMouseAnimation = async (isActive: boolean) => {
    if (speakerPanelIdRef.current) {
      await window.dsDesktop.speakerPanel.updateData(
        speakerPanelIdRef.current,
        {
          isMouseHighlightActive: isActive,
        }
      );
      window.dsDesktop.demostacking.updateAnimationSettings({ on: isActive });
    }
  };

  const setSpeakerWindowBounds = async () => {
    if (!speakerViewRef.current) return;

    const clientRect = speakerViewRef.current.ref?.getBoundingClientRect();
    if (!clientRect) return;
    const dimensions = {
      width: Math.round(clientRect.width),
      height: Math.round(clientRect.height),
      x: Math.round(clientRect.left),
      y: Math.round(clientRect.top),
    };
    await window.dsDesktop.layout.resizeFloatingWindow(dimensions);
  };

  const initSpeakerPanel = async () => {
    const currentTab = await window.dsDesktop?.tabs?.getCurrentTab();
    if (currentTab?.group?.id) {
      // init speakerPanelIdRef
      speakerPanelIdRef.current = currentTab.group.id;
      // init data
      const updatedData = await window.dsDesktop?.speakerPanel?.updateData(
        currentTab.group.id,
        {
          isOpen: true,
        }
      );
      setSpeakerPanelData(updatedData);
      setShowSpeakerPanel(true);
      await window.dsDesktop.speakerPanel.setToggleButtonState(true);
    }
  };

  const handleDetach = async (index: number) => {
    if (speakerPanelIdRef.current) {
      await window.dsDesktop.speakerPanel.detach(
        speakerPanelIdRef.current,
        index
      );
    }
  };

  const handleSetActive = async (index: number) => {
    if (speakerPanelIdRef.current) {
      setActiveItem(index);
      await window.dsDesktop.speakerPanel.setActiveItem(
        speakerPanelIdRef.current,
        index
      );
    }
  };

  const handleSpeakerViewResize = () => {
    if (speakerViewRef.current) {
      setSpeakerWindowBounds();

      observerRef.current = new ResizeObserver(() => {
        setIsOpen((prevState) => {
          return !prevState;
        });
      });
      if (speakerViewRef.current.ref)
        observerRef.current.observe(speakerViewRef?.current?.ref);
    }

    return () => observerRef?.current?.disconnect();
  };

  const handleOnToggleShowDemos = async (showDemos: boolean) => {
    if (speakerPanelIdRef.current) {
      await window.dsDesktop.speakerPanel.updateData(
        speakerPanelIdRef.current,
        {
          isWindowOpen: showDemos,
        }
      );
    }
  };

  const handleOnToggleShowNotes = async (showNotes: boolean) => {
    if (speakerPanelIdRef.current) {
      await window.dsDesktop.speakerPanel.updateData(
        speakerPanelIdRef.current,
        {
          isNotesOpen: showNotes,
        }
      );
    }
  };

  useEffect(() => {
    initSpeakerPanel();
  }, []);

  useLayoutEffect(() => {
    setSpeakerWindowBounds();
  }, [isOpen, speakerViewRef.current?.isNotesOpen]);

  useEffect(() => {
    handleSpeakerViewResize();
    return () => {
      if (observerRef.current && speakerViewRef?.current?.ref) {
        observerRef.current.disconnect();
        observerRef.current.unobserve(speakerViewRef?.current?.ref);
      }
    };
  }, [speakerPanelData]);

  useElectronListener(
    SpeakerPanelEventsEnum.MAIN_TO_SPEAKER_PANEL_CLOSE_FROM_SPEAKER_PANEL,
    (...args: unknown[]) => {
      const options = args[0] as { resetData: boolean };
      handleClose(options);
    }
  );

  useElectronListener(
    SpeakerPanelEventsEnum.MAIN_TO_SPEAKER_PANEL_UPDATED_DATA,
    (...args: unknown[]) => {
      const updatedSpeakerPanelData = args[0] as SpeakerPanelData;
      if (!updatedSpeakerPanelData.demosInfo) return;
      setSpeakerPanelData(updatedSpeakerPanelData);
      setActiveItem(updatedSpeakerPanelData.activePresentationIndex);
    }
  );

  useElectronListener(
    SpeakerPanelEventsEnum.MAIN_TO_SPEAKER_PANEL_REINITIALIZE,
    () => {
      initSpeakerPanel();
    },
    [speakerPanelIdRef.current]
  );

  useElectronListener(
    SpeakerPanelEventsEnum.MAIN_TO_SPEAKER_PANEL_SWITCH_WITH_KEYBOARD_SHORTCUT,
    (arg) => {
      const direction = arg as 'forward' | 'backward';
      if (direction === 'forward') setActiveItem((prev) => prev + 1);
      if (direction === 'backward') setActiveItem((prev) => prev - 1);
    },
    [speakerViewRef]
  );

  if (!speakerPanelData || !showSpeakerPanel) return <div />;
  return (
    <SpeakerViewScreen
      onMouseHighlightClick={updateMouseAnimation}
      ref={speakerViewRef}
      startTime={speakerPanelData.currentTime}
      onClose={() => {
        handleClose({ resetData: true });
      }}
      onToggleShowDemos={handleOnToggleShowDemos}
      onToggleShowNotes={handleOnToggleShowNotes}
      activeItem={activeItem}
      onSetActive={handleSetActive}
      onDetach={handleDetach}
      items={speakerPanelData ? speakerPanelData.demosInfo : []}
      shouldShowNotes={speakerPanelData.isNotesOpen}
      shouldShowWindows={speakerPanelData.isWindowOpen}
      shouldShowMouseHighlight={speakerPanelData.isMouseHighlightActive}
      noDrag={{
        enabled: true,
        toggleEnabled: !window?.dsDesktop?.app?.isWindows,
      }}
    />
  );
};

export default SpeakerPanelScreen;
